import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactTableWrapper from '../../components/ReactTableWrapper';
import ListPageLayout from '../../components/ListPageLayout';
import { exportAsCSV } from '../../utils/CSVExporter';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import { buildCustomerLedgerPdfHeader } from '../../utils/customerLedgerPdf';
import { exportAsImage } from '../../utils/ImageExporter';
import Sidebar from '../../Sidebar';
import { useCookies } from 'react-cookie';
import { Button } from 'reactstrap';
import Select from 'react-select';
import { DatePicker } from 'antd';
import moment from 'moment';
import { getAllPlants } from '../../services/PlantServices';
import { getAllCustomerMain } from '../../services/CustomerMainServices';
import { getLedgerAccountReport } from '../../services/ReportServices';
import { displayError } from '../../helpers';
import { HandleSelect, reportMultiSelectComponents, reportFilterSelectStyles } from '../../components/MultiSelectControls';
import ReportExportDropdown from '../../components/ReportExportDropdown';

const { RangePicker } = DatePicker;

const filterSelectStyles = reportFilterSelectStyles;

function LedgerAccount() {
    const [cookies] = useCookies(['myToken']);
    const exportRef = useRef(null);
    const [records, setRecords] = useState([]);
    const [summary, setSummary] = useState({
        opening_balance: 0,
        closing_balance: 0,
        total_debit: 0,
        total_credit: 0,
    });
    const [plants, setPlants] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [selectedPlant, setSelectedPlant] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [fromDate, setFromDate] = useState(moment().startOf('month'));
    const [toDate, setToDate] = useState(moment());
    const [isLoading, setIsLoading] = useState(false);
    const [searchText, setSearchText] = useState('');

    const plantsWithSelectAll = useMemo(
        () => [{ value: 'select-all', label: 'Select All plants' }, ...plants],
        [plants],
    );

    const resolvePlantIds = (selected, allOptions) => {
        if (!selected || selected.length === 0) {
            return '*';
        }
        if (selected.some((item) => item.value === 'select-all')) {
            return allOptions.map((item) => item.value).join(',') || '*';
        }
        return selected.map((item) => item.value).join(',');
    };

    useEffect(() => {
        getAllPlants(cookies).then((plantList) => {
            setPlants((plantList.plant_list || [])
                .filter((plant) => plant.status)
                .map((plant) => ({ value: plant.id, label: plant.plant_alias })));
        });
        getAllCustomerMain(cookies).then((customerList) => {
            setCustomers((customerList.customer_list || [])
                .filter((customer) => customer.status)
                .map((customer) => ({ value: customer.id, label: customer.name })));
        });
    }, [cookies]);

    const fetchLedger = useCallback(() => {
        if (!selectedCustomer?.value || !fromDate?.isValid() || !toDate?.isValid()) {
            displayError({ message: 'Select a customer and date range.' }, 'Load Failed');
            return Promise.resolve();
        }
        setIsLoading(true);
        return getLedgerAccountReport(cookies, {
            plant_id: resolvePlantIds(selectedPlant, plants),
            customer_ids: String(selectedCustomer.value),
            from_date: fromDate.format('DD-MM-YYYY'),
            upto_date: toDate.format('DD-MM-YYYY'),
        })
            .then((response) => {
                setRecords(response.customer_ledger_list || []);
                setSummary({
                    opening_balance: response.opening_balance || 0,
                    closing_balance: response.closing_balance || 0,
                    total_debit: response.total_debit || 0,
                    total_credit: response.total_credit || 0,
                });
            })
            .catch((error) => {
                displayError(error?.response?.data || error, 'Load Failed');
                setRecords([]);
            })
            .finally(() => setIsLoading(false));
    }, [cookies, fromDate, plants, selectedCustomer, selectedPlant, toDate]);

    const columns = useMemo(() => [
        { fieldName: 'customer_name', headerName: 'Customer' },
        { fieldName: 'voucher_date', headerName: 'Voucher Date' },
        { fieldName: 'voucher_type', headerName: 'Voucher Type' },
        { fieldName: 'voucher_no', headerName: 'Voucher No' },
        { fieldName: 'grade', headerName: 'Grade' },
        { fieldName: 'site_name', headerName: 'Site Name' },
        { fieldName: 'mode', headerName: 'Mode' },
        { fieldName: 'quantity', headerName: 'Quantity' },
        { fieldName: 'debit', headerName: 'Debit' },
        { fieldName: 'credit', headerName: 'Credit' },
        { fieldName: 'balance', headerName: 'Balance' },
    ], []);

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        if (!query) {
            return records;
        }
        const terms = query.split(/\s+/).filter(Boolean);
        return records.filter((row) => {
            const haystack = Object.values(row)
                .filter((value) => value != null && value !== '')
                .map((value) => String(value).toLowerCase())
                .join(' ');
            return terms.every((term) => haystack.includes(term));
        });
    }, [records, searchText]);

    const formatAmount = (value) => Number(value || 0).toFixed(2);

    const summaryStats = useMemo(() => [
        { label: 'OPENING', value: formatAmount(summary.opening_balance), accent: 'b2b' },
        { label: 'DEBIT', value: formatAmount(summary.total_debit), accent: 'yellow' },
        { label: 'CREDIT', value: formatAmount(summary.total_credit), accent: 'b2c' },
        { label: 'CLOSING', value: formatAmount(summary.closing_balance), accent: 'b2b' },
    ], [summary]);

    const pdfSummaryLines = useMemo(() => [
        `Opening: ${formatAmount(summary.opening_balance)}    Debit: ${formatAmount(summary.total_debit)}    Credit: ${formatAmount(summary.total_credit)}    Closing: ${formatAmount(summary.closing_balance)}`,
    ], [summary]);

    const exportFileBase = useMemo(() => {
        const customerPart = (selectedCustomer?.label || 'Customer')
            .replace(/[^\w\-]+/g, '_')
            .slice(0, 40);
        const fromPart = fromDate?.isValid() ? fromDate.format('DD-MM-YYYY') : '';
        const toPart = toDate?.isValid() ? toDate.format('DD-MM-YYYY') : '';
        return `CustomerLedger_${customerPart}_${fromPart}_${toPart}`;
    }, [fromDate, selectedCustomer, toDate]);

    const ensureExportData = () => {
        if (!filteredRecords.length) {
            displayError({ message: 'Generate the report first, then export.' }, 'Export Failed');
            return false;
        }
        return true;
    };

    const handleCSVExport = () => {
        if (!ensureExportData()) {
            return;
        }
        exportAsCSV(columns, filteredRecords, exportFileBase);
    };

    const handleExcelExport = () => {
        if (!ensureExportData()) {
            return;
        }
        exportAsExcel(columns, filteredRecords, exportFileBase);
    };

    const pdfColumns = useMemo(
        () => columns
            .filter((col) => col.fieldName !== 'customer_name')
            .map((col) => ({ header: col.headerName, dataKey: col.fieldName })),
        [columns],
    );

    const pdfTitle = useMemo(
        () => `${selectedCustomer?.label || ''} (${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')})`,
        [fromDate, selectedCustomer, toDate],
    );

    const handlePDFExport = async () => {
        if (!ensureExportData()) {
            return;
        }
        setIsLoading(true);
        try {
            const header = await buildCustomerLedgerPdfHeader(cookies, selectedPlant, plants, {
                subtitle: pdfTitle,
            });
            const blob = createPdfBlob('', pdfColumns, filteredRecords, pdfSummaryLines, header);
            openPdfInApp(blob, {
                title: 'Customer Ledger',
                downloadFileName: `${exportFileBase}.pdf`,
            });
        } catch (error) {
            displayError({ message: error?.message || 'PDF export failed.' }, 'Export Failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleImageExport = async () => {
        if (!ensureExportData()) {
            return;
        }
        const element = exportRef.current;
        element?.classList.add('is-exporting-image');
        try {
            await exportAsImage(element, `${exportFileBase}.png`);
        } catch (error) {
            displayError({ message: error?.message || 'Image export failed.' }, 'Export Failed');
        } finally {
            element?.classList.remove('is-exporting-image');
        }
    };

    const toolbarFilters = (
        <>
            <div className="list-page-toolbar-filters-row">
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter list-page-toolbar-filter-plant">
                        <Select
                            isMulti
                            closeMenuOnSelect={false}
                            hideSelectedOptions={false}
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            options={plantsWithSelectAll}
                            value={selectedPlant}
                            onChange={(selected) => HandleSelect(
                                selected,
                                selectedPlant,
                                setSelectedPlant,
                                plants,
                                plantsWithSelectAll,
                            )}
                            placeholder="Plant"
                            styles={reportFilterSelectStyles}
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter">
                        <Select
                            classNamePrefix="list-page-filter-select"
                            components={reportMultiSelectComponents}
                            options={customers}
                            value={selectedCustomer}
                            onChange={setSelectedCustomer}
                            placeholder="Customer *"
                            styles={reportFilterSelectStyles}
                        />
                    </div>
                </div>
                <div className="list-page-toolbar-slot">
                    <div className="list-page-toolbar-filter-date">
                        <RangePicker
                            value={[fromDate, toDate]}
                            format="DD-MM-YYYY"
                            onChange={(values) => {
                                if (values && values.length === 2) {
                                    setFromDate(values[0]);
                                    setToDate(values[1]);
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
            <div className="list-page-toolbar-filters-row list-page-toolbar-filters-row-actions">
                <ReportExportDropdown
                    onCSV={handleCSVExport}
                    onExcel={handleExcelExport}
                    onPDF={handlePDFExport}
                    onImage={handleImageExport}
                    disabled={isLoading || !filteredRecords.length}
                />
                <Button
                    color="primary"
                    className="list-page-toolbar-generate-btn"
                    onClick={fetchLedger}
                    disabled={isLoading}
                >
                    Generate
                </Button>
            </div>
        </>
    );

    return (
        <div id="outer-container" className="App">
            <Sidebar pageWrapId="page-wrap" outerContainerId="outer-container" />
            <div id="page-wrap">
                <ListPageLayout
                    moduleLabel="ACCOUNTS"
                    title="Customer Ledger"
                    subtitle="Sundry debtors statement — invoices, receipts, credit/debit notes, and refunds."
                    modeLabel="REPORT"
                    toolbarVariant="stacked"
                    recordCount={filteredRecords.length}
                    recordLabel="ledger lines"
                    summaryStats={summaryStats}
                    searchText={searchText}
                    onSearchChange={setSearchText}
                    searchPlaceholder="Search voucher, narration, amount..."
                    toolbarFilters={toolbarFilters}
                    reportListLink="/ReportAll"
                >
                    <div ref={exportRef} className="ledger-export-capture">
                        <div className="ledger-export-meta">
                            <strong>Customer Ledger</strong>
                            {selectedCustomer?.label ? ` — ${selectedCustomer.label}` : ''}
                            {fromDate?.isValid() && toDate?.isValid()
                                ? ` (${fromDate.format('DD-MM-YYYY')} to ${toDate.format('DD-MM-YYYY')})`
                                : ''}
                            <span className="ledger-export-summary">
                                Opening: {formatAmount(summary.opening_balance)}
                                {' | '}
                                Debit: {formatAmount(summary.total_debit)}
                                {' | '}
                                Credit: {formatAmount(summary.total_credit)}
                                {' | '}
                                Closing: {formatAmount(summary.closing_balance)}
                            </span>
                        </div>
                        <div className="item-list-table-container">
                        <div className="table-responsive">
                            <ReactTableWrapper
                                title="Customer Ledger"
                                columns={columns}
                                data={filteredRecords}
                                hideDelete
                                hidePrint
                                onCSVExport={handleCSVExport}
                                onPDFExport={handlePDFExport}
                                enableTableSearch={false}
                                isLoading={isLoading}
                                compactRows
                            />
                        </div>
                        </div>
                    </div>
                </ListPageLayout>
            </div>
        </div>
    );
}

export default LedgerAccount;
